// IndexedDB 封装 for PWA 离线学习 (split 分支)
// 三个 store:
//   - words:    全量词库, keyPath=id, 索引 new_group_id (按拆分组查询)
//   - progress: 离线进度, keyPath=`${new_group_id}:${op_type}`, {key, new_group_id, group_id, op_type, completed_at, dirty}
//   - meta:     元数据, keyPath=key (lastSyncAt, learnableGroups[])
// split 使用独立 DB 名, 与 V1(words-pwa) 在同一浏览器源下互不干扰。

const DB_NAME = 'words-split-pwa';
const DB_VERSION = 2;

function _parentOf(ng) {
    try { return String(ng).split('.')[0]; } catch (e) { return String(ng); }
}

let dbPromise = null;

function openDB() {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = (e) => {
            const db = e.target.result;
            if (!db.objectStoreNames.contains('words')) {
                db.createObjectStore('words', { keyPath: 'id' })
                  .createIndex('new_group_id', 'new_group_id', { unique: false });
            } else {
                // 升级: 旧库可能没有 new_group_id 索引
                const ws = e.target.transaction.objectStore('words');
                if (!ws.indexNames.contains('new_group_id')) {
                    ws.createIndex('new_group_id', 'new_group_id', { unique: false });
                }
            }
            if (!db.objectStoreNames.contains('progress')) {
                db.createObjectStore('progress', { keyPath: 'key' });
            }
            if (!db.objectStoreNames.contains('meta')) {
                db.createObjectStore('meta', { keyPath: 'key' });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
    return dbPromise;
}

// 通用: 单 store 事务执行, 返回主请求结果
async function singleReq(store, mode, fn) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = fn(t.objectStore(store));
        t.oncomplete = () => resolve(req.result);
        t.onerror = () => reject(t.error);
    });
}

// ---------- words store ----------

export async function putWords(wordsArr) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const t = db.transaction('words', 'readwrite');
        const s = t.objectStore('words');
        wordsArr.forEach(w => s.put(w));
        t.oncomplete = () => resolve();
        t.onerror = () => reject(t.error);
    });
}

export async function getWordsByGroup(groupId) {
    // split: groupId 实为 new_group_id(如 '69.1'), 按 new_group_id 索引查询
    return singleReq('words', 'readonly', s =>
        s.index('new_group_id').getAll(IDBKeyRange.only(String(groupId)))
    );
}

export async function getWordsCount() {
    return singleReq('words', 'readonly', s => s.count());
}

export async function getAllWordsFromIDB() {
    return singleReq('words', 'readonly', s => s.getAll());
}

// ---------- progress store ----------

export async function getProgress(new_group_id, op_type) {
    return singleReq('progress', 'readonly', s => s.get(`${new_group_id}:${op_type}`));
}

export async function getAllProgress() {
    return singleReq('progress', 'readonly', s => s.getAll());
}

// split: 进度以 new_group_id 为单元; group_id 由 new_group_id 推导(父组号)一并存储
export async function putProgress(new_group_id, op_type, completed_at, dirty, duration_sec = 0) {
    const key = `${new_group_id}:${op_type}`;
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const t = db.transaction('progress', 'readwrite');
        t.objectStore('progress').put({
            key,
            new_group_id: String(new_group_id),
            group_id: _parentOf(new_group_id),
            op_type,
            completed_at,
            dirty,
            duration_sec: duration_sec || 0,
        });
        t.oncomplete = () => resolve();
        t.onerror = () => reject(t.error);
    });
}

export async function getDirtyProgress() {
    const all = await getAllProgress();
    return all.filter(p => p.dirty);
}

export async function clearDirty(new_group_id, op_type) {
    const key = `${new_group_id}:${op_type}`;
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const t = db.transaction('progress', 'readwrite');
        const s = t.objectStore('progress');
        const req = s.get(key);
        req.onsuccess = () => {
            if (req.result) { req.result.dirty = false; s.put(req.result); }
        };
        t.oncomplete = () => resolve();
        t.onerror = () => reject(t.error);
    });
}

// 删除某条进度(用于服务端已删除记录回写本地, 实现撤销/未执行)
export async function deleteProgress(new_group_id, op_type) {
    const key = `${new_group_id}:${op_type}`;
    return singleReq('progress', 'readwrite', s => s.delete(key));
}

// ---------- meta store ----------

export async function getMeta(key) {
    const r = await singleReq('meta', 'readonly', s => s.get(key));
    return r ? r.value : undefined;
}

export async function setMeta(key, value) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const t = db.transaction('meta', 'readwrite');
        t.objectStore('meta').put({ key, value });
        t.oncomplete = () => resolve();
        t.onerror = () => reject(t.error);
    });
}

export async function getLearnableGroups() {
    return (await getMeta('learnableGroups')) || [];
}

export async function setLearnableGroups(groups) {
    return setMeta('learnableGroups', groups);
}

// 已初始化 = 本地已完成过一次云端初始化 (lastSyncAt 存在)
// 首页/组件用它判断, 而不是"能不能构造出首页表格"
export async function isInitialized() {
    return !!(await getMeta('lastSyncAt'));
}
